import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import {
  Coordinate,
  londonCenterCoordinates,
  MapMarker,
  MapMarkersDefaultSettings,
  MapMarkerType,
  RoutesColors,
  RouteWayPoint,
} from '../../utils/mapUtils';
import { defaultGoogleMapsOptions, MapOptions, zoomControlGoogleMapsOptions } from '../../utils/googleMapUtils';
import { firstValueFrom, forkJoin, from, lastValueFrom, Observable, of, zip } from 'rxjs';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Clipboard as AngularClipboard } from '@angular/cdk/clipboard';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { take } from 'rxjs/operators';

@Component({
  selector: 'common-map',
  standalone: true,
  imports: [CommonModule, GoogleMapsModule, HttpClientModule, MatSnackBarModule],
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements AfterViewInit, OnChanges, OnInit {
  @ViewChild('map') mapElement!: GoogleMap;
  @ViewChildren('markers') mapMarkersElements!: QueryList<google.maps.marker.AdvancedMarkerElement>;
  @Input() mapMarkers?: MapMarker[];
  @Input() mapMarkersSettings = MapMarkersDefaultSettings;
  @Input() fitBoundsToAllMarkers?: boolean;
  @Input() boundsPadding?: { top: number; bottom: number; right: number; left: number };
  @Input() center: Coordinate = londonCenterCoordinates;
  @Input() adminsCopyAddressOnClick = false;
  @Input() zoom = 18;
  @Output() mapClick = new EventEmitter();
  @Output() markerClick = new EventEmitter<MapMarker>();
  @Output() markerMouseIn = new EventEmitter<MapMarker>();
  @Output() markerMouseOut = new EventEmitter<MapMarker>();
  @Input() mapOptions?: MapOptions;
  @Input() markersLabelClass?: string;
  googleMapOptions: google.maps.MapOptions = {};
  googleMapMarkers: { [id: string]: google.maps.marker.AdvancedMarkerElement | any } = {};
  infoWindows: google.maps.InfoWindow[] = [];
  routeRenderers: { renderer: google.maps.DirectionsRenderer; response: google.maps.DirectionsResult | null }[] = [];
  constructor(
    private http: HttpClient,
    private clipboard: AngularClipboard,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    this.googleMapOptions = {
      mapId: 'DEMO_MAP_ID',
      ...defaultGoogleMapsOptions,
      ...(this.mapOptions?.zoomControl ? zoomControlGoogleMapsOptions : {}),
    };
  }

  ngAfterViewInit(): void {
    if (this.fitBoundsToAllMarkers) {
      this.deprecatedFitMapToAllMarker();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['mapMarkers']) {
      this.buildMapMarkers();
      this.fitBoundsToAllMarkers && this.deprecatedFitMapToAllMarker();
    }
    if (changes['boundsPadding']) {
      this.fitBoundsToAllMarkers && this.deprecatedFitMapToAllMarker();
    }
  }

  buildMapMarkers() {
    const iconsRequests =
      this.mapMarkers?.reduce((acc: Observable<string>[], marker) => {
        if (this.mapMarkersSettings[marker.type]?.iconName) {
          acc.push(
            this.http.get(`assets/mapMarkers/${this.mapMarkersSettings[marker.type].iconName}.svg`, {
              responseType: 'text',
            }),
          );
        }
        return acc;
      }, []) || [];

    zip(iconsRequests).subscribe((value) => {
      this.clearMapMarkers();
      this.mapMarkers?.forEach((marker, i) => {
        const markerSvg = marker.color ? value[i]?.replace('currentColor', marker.color) : value[i];
        this.googleMapMarkers[marker.id] = this.buildGoogleMapMarker(marker, markerSvg);
      });
    });
  }

  clearMapMarkers() {
    Object.keys(this.googleMapMarkers).forEach((markerId) => this.removeMarker(markerId));
  }

  //Todo replace

  deprecatedFitMapToAllMarker() {
    if (this.mapMarkers?.length) {
      const mapBounds = new google.maps.LatLngBounds();
      this.mapMarkers?.forEach((marker) => {
        mapBounds.extend(marker.coordinate);
      });
      this.mapElement?.fitBounds(mapBounds, this.boundsPadding);
      // Workaround to fix the map padding issue when there is only one marker (google bug)
      if (this.mapMarkers.length === 1) {
        setTimeout(() => {
          this.boundsPadding?.bottom && this.mapElement?.panBy(0, 25);
        }, 200);
      }
    }
  }

  fitMapToAllMarker() {
    const markerKeys = Object.keys(this.googleMapMarkers);
    if (markerKeys?.length) {
      const mapBounds = new google.maps.LatLngBounds();
      markerKeys.forEach((key) => {
        const position = this.googleMapMarkers[key].position;
        mapBounds.extend(position);
      });
      this.mapElement?.fitBounds(mapBounds, this.boundsPadding);
    }
  }

  centerMap(center: google.maps.LatLng) {
    this.mapElement?.googleMap?.panTo(center);
  }

  zoomMap(zoom: number) {
    const currentZoom = this.mapElement.googleMap?.getZoom() || 0;
    this.mapElement?.googleMap?.setZoom(currentZoom + zoom);
  }

  public fitMapBounds(coordinate: Coordinate[]) {
    const mapBounds = new google.maps.LatLngBounds();
    coordinate.forEach((c) => mapBounds.extend(c));
    this.mapElement?.fitBounds(mapBounds, this.boundsPadding);
  }

  public async addMapMarkers(markers: MapMarker[]) {
    if (!markers.length) return;
    const observables: any = [];
    markers.forEach((marker) => {
      observables.push(from(this.addMarker(marker)));
    });
    return await firstValueFrom(forkJoin(observables));
  }

  public async addMarker(marker: MapMarker) {
    if (this.mapMarkersSettings[marker.type]?.iconName) {
      const request$ = this.http
        .get(`assets/mapMarkers/${this.mapMarkersSettings[marker.type].iconName}.svg`, { responseType: 'text' })
        .pipe(take(1));
      const response = await lastValueFrom(request$);
      const markerSvg = marker.color ? response?.replace('currentColor', marker.color) : response;
      let responseHoverIcon;
      if (this.mapMarkersSettings[marker.type]?.iconNameHover) {
        const requestHoverIcon$ = this.http
          .get(`assets/mapMarkers/${this.mapMarkersSettings[marker.type].iconNameHover}.svg`, { responseType: 'text' })
          .pipe(take(1));
        responseHoverIcon = await lastValueFrom(requestHoverIcon$);
      }
      this.googleMapMarkers[marker.id] = await this.buildGoogleMapMarker(marker, markerSvg, responseHoverIcon);
      return;
    } else {
      this.googleMapMarkers[marker.id] = await this.buildGoogleMapMarker(marker);
      return;
    }
  }

  public removeMarker(markerId: string) {
    this.googleMapMarkers[markerId].map = null;
  }

  public moveMarker(markerId: string, position: Coordinate) {
    const wayPoints = 100;
    const delayMs = 10;

    const marker = this.googleMapMarkers[markerId];
    const markerPosition: any = marker && this.googleMapMarkers[markerId].position;
    if (marker && markerPosition) {
      const deltaLat = (position.lat - markerPosition.lat) / wayPoints;
      const deltaLng = (position.lng - markerPosition.lng) / wayPoints;
      let currentPosition = { lat: markerPosition.lat, lng: markerPosition.lng };
      for (let i = 0; i < wayPoints; i++) {
        currentPosition = { lat: currentPosition.lat + deltaLat, lng: currentPosition.lng + deltaLng };
        this.setMarkerPositionWithDelay(marker, currentPosition, i * delayMs);
      }
    }
  }

  setMarkerPositionWithDelay(marker: google.maps.marker.AdvancedMarkerElement, position: Coordinate, delay: number) {
    setTimeout(() => {
      marker.position = position;
    }, delay);
  }

  async buildGoogleMapMarker(marker: MapMarker, markerSvg?: string, hoverSvg?: string) {
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
    const newMarker = new AdvancedMarkerElement({
      zIndex: this.mapMarkersSettings[marker.type]?.zIndex,
      content: markerSvg ? this.getMarkerContent(markerSvg, marker.label, marker.width, marker.height) : null,
      position: marker.coordinate,
      map: this.mapElement.googleMap,
    });
    if (marker.infoWindowText) {
      const infoWindow = new google.maps.InfoWindow({
        content: marker.infoWindowText,
      });
      this.infoWindows.push(infoWindow);
      if (marker.disableInfoWindowOpenClose) {
        infoWindow.open({
          anchor: newMarker,
          map: this.mapElement.googleMap,
        });
      } else {
        newMarker.addListener('click', () => {
          this.infoWindows.forEach((i) => i.close());
          infoWindow.open({
            anchor: newMarker,
            map: this.mapElement.googleMap,
          });
        });
      }
    } else if (marker.clickCustomEvent) {
      // newMarker.addListener('click', () => {
      //
      // });
    }
    if (marker.hoverCustomEvent) {
      if (markerSvg && hoverSvg) this.setIconHoverEvent(newMarker, marker, markerSvg, hoverSvg);
    }
    return newMarker;
  }

  setIconHoverEvent(
    newMarker: google.maps.marker.AdvancedMarkerElement,
    mapMarker: MapMarker,
    markerSvg: string,
    hoverSvg: string,
  ) {
    newMarker.addListener('click', () => {});
    newMarker.content?.addEventListener('mouseenter', () => {
      this.markerMouseIn.emit(mapMarker);
      newMarker.content = this.getMarkerContent(hoverSvg, mapMarker.label, mapMarker.width, mapMarker.height);
      newMarker.addListener('click', () => {});
      newMarker.content?.addEventListener('mouseleave', () => {
        this.markerMouseOut.emit(mapMarker);
        newMarker.content = this.getMarkerContent(markerSvg, mapMarker.label, mapMarker.width, mapMarker.height);
        this.setIconHoverEvent(newMarker, mapMarker, markerSvg, hoverSvg);
      });
    });
  }

  getMarkerContent(markerSvg: string, label?: string, width?: number, height?: number) {
    const contentElement = document.createElement('div');
    if (height) contentElement.style.height = height + 'px';
    if (width) contentElement.style.width = width + 'px';
    contentElement.innerHTML = markerSvg;
    if (label !== undefined) {
      const elementLabel = document.createElement('span');
      elementLabel.innerHTML = label;
      if (this.markersLabelClass && this.markersLabelClass.length > 0) {
        elementLabel.classList.add(this.markersLabelClass);
      }
      contentElement.appendChild(elementLabel);
    }
    return contentElement;
  }

  onMapClick(e: any) {
    if (this.adminsCopyAddressOnClick) {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        {
          location: e.latLng,
        },
        (results, status) => {
          if (status == google.maps.GeocoderStatus.OK && results?.length) {
            this.clipboard.copy(results[0].formatted_address);
            this.snackBar.dismiss();
            this.snackBar.open('Copied to clipboard');
            setTimeout(() => {
              this.snackBar.dismiss();
            }, 1500);
          }
        },
      );
    }
    this.mapClick.emit(e);
  }

  cleanRoutes() {
    this.routeRenderers.forEach((render) => {
      render.renderer.setMap(null);
    });
    this.routeRenderers = [];
  }

  drawRoutes(origin: Coordinate, destination: Coordinate, index: number, waypoints?: RouteWayPoint[], color?: string) {
    const directionsService = new google.maps.DirectionsService();
    const request: google.maps.DirectionsRequest = {
      destination,
      origin,
      travelMode: google.maps.TravelMode.DRIVING,
      waypoints: waypoints,
    };
    directionsService.route(request, (response, status) => {
      if (status == google.maps.DirectionsStatus.OK) {
        this.renderRoute(response, index, color);
        this.setRouteMarkers(response!.routes[0].legs, index, color);
      }
    });
  }

  renderRoute(response: google.maps.DirectionsResult | null, colorIndex: number, color?: string) {
    const renderer = new google.maps.DirectionsRenderer();
    renderer?.setOptions({
      suppressPolylines: false,
      suppressInfoWindows: true,
      map: this.mapElement.googleMap,
      polylineOptions: {
        strokeColor: color,
        strokeWeight: 4,
      },
      suppressMarkers: true,
    });
    renderer?.setDirections(response);
    this.routeRenderers.push({ renderer, response });
  }

  async setRouteMarkers(legs: google.maps.DirectionsLeg[], routeIndex: number, color?: string) {
    const routeGoogleMapMarkers: MapMarker[] = [];
    //Add route dropoffs markers
    legs.forEach((leg: any, index: number) => {
      routeGoogleMapMarkers!.push({
        routeIndex: routeIndex,
        width: 34,
        height: 53,
        color: color,
        id: `route-dropoff-${routeIndex}-${index}`,
        coordinate: leg.end_location.toJSON(),
        type: MapMarkerType.routeDropoff,
        label: (index + 1).toString(),
      });
    });

    await this.addMapMarkers(routeGoogleMapMarkers);
    this.fitMapToAllMarker();
    setTimeout(() => {
      this.zoomMap(-0.2);
    });
  }
}
